import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Map as MapIcon, List, Shield, Info, Download, Sun, Moon, Bell, BellOff, Megaphone } from "lucide-react";
import MapView from "@/components/MapView";
import EventSheet from "@/components/EventSheet";
import EventCard from "@/components/EventCard";
import AroundMeBar from "@/components/AroundMeBar";
import Filters from "@/components/Filters";
import ProposeDialog from "@/components/ProposeDialog";
import { Footer } from "@/components/Footer";
import { getDays, getEvents } from "@/lib/api";
import { haversine, RELIABILITY } from "@/lib/events";

export default function MainApp() {
  const [days, setDays] = useState([]);
  const [dayId, setDayId] = useState("");
  const [events, setEvents] = useState([]);
  const [view, setView] = useState("map");
  const [selected, setSelected] = useState(null);
  const [focusId, setFocusId] = useState(null);
  const [userLoc, setUserLoc] = useState(null);
  const [radius, setRadius] = useState(25);
  const [filters, setFilters] = useState({ type: "", status: "", reliability: "", department: "" });
  const [installPrompt, setInstallPrompt] = useState(null);
  const [notifEnabled, setNotifEnabled] = useState(() => (typeof localStorage !== "undefined" && localStorage.getItem("am_notif") === "1"));
  const [showPropose, setShowPropose] = useState(false);
  const [now, setNow] = useState(new Date());
  const knownIdsRef = useRef(null);
  const [theme, setTheme] = useState(() => {
    const stored = typeof localStorage !== "undefined" && localStorage.getItem("am_theme");
    if (stored) return stored;
    return (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) ? "dark" : "light";
  });

  useEffect(() => {
    getDays().then((d) => {
      setDays(d);
      const active = d.find((x) => x.status === "en_cours") || d.find((x) => x.status === "a_venir") || d[0];
      if (active) setDayId(active.id);
    });
  }, []);

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const loadEvents = () => {
    const params = {};
    if (dayId) params.day_id = dayId;
    const ad = days.find((d) => d.id === dayId);
    if (ad && ["terminee", "archivee"].includes(ad.status)) params.active = false;
    if (filters.type) params.type = filters.type;
    if (filters.status) params.status = filters.status;
    if (filters.reliability) params.reliability = filters.reliability;
    if (filters.department) params.department = filters.department;
    getEvents(params).then(setEvents).catch(() => toast.error("Erreur de chargement"));
  };

  useEffect(() => {
    if (days.length === 0) return;
    loadEvents();
    const t = setInterval(loadEvents, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [dayId, filters, days.length]);

  useEffect(() => {
    if (!events) return;
    const ids = new Set(events.map((e) => e.id));
    if (knownIdsRef.current === null) { knownIdsRef.current = ids; return; }
    if (notifEnabled && typeof Notification !== "undefined" && Notification.permission === "granted") {
      events
        .filter((e) => !knownIdsRef.current.has(e.id) && e.type === "blocage_routier" && e.status !== "termine")
        .forEach((e) => {
          if (userLoc && haversine(userLoc.lat, userLoc.lng, e.lat, e.lng) > radius) return;
          try { new Notification("Alliance Manif — Nouveau blocage", { body: `${e.title} · ${e.city}`, icon: "/am-icon.png" }); } catch (err) {}
        });
    }
    knownIdsRef.current = ids;
  }, [events, notifEnabled, userLoc, radius]);

  const departments = useMemo(() => {
    return [...new Set(events.map((e) => e.department).filter(Boolean))].sort();
  }, [events]);

  const listEvents = useMemo(() => {
    let list = [...events];
    if (userLoc) {
      list = list
        .map((e) => ({ ...e, distance_km: Math.round(haversine(userLoc.lat, userLoc.lng, e.lat, e.lng) * 10) / 10 }))
        .filter((e) => e.distance_km <= radius)
        .sort((a, b) => a.distance_km - b.distance_km);
    }
    return list;
  }, [events, userLoc, radius]);

  const mapEvents = userLoc ? listEvents : events;
  const activeDay = days.find((d) => d.id === dayId);
  const isArchive = activeDay && ["terminee", "archivee"].includes(activeDay.status);

  const doInstall = async () => {
    if (!installPrompt) return toast.info("Utilisez le menu du navigateur › « Ajouter à l'écran d'accueil »");
    installPrompt.prompt();
    setInstallPrompt(null);
  };

  const toggleNotif = async () => {
    if (notifEnabled) { setNotifEnabled(false); localStorage.setItem("am_notif", "0"); toast.info("Alertes blocages désactivées"); return; }
    if (!("Notification" in window)) return toast.error("Notifications non supportées par ce navigateur");
    let perm = Notification.permission;
    if (perm !== "granted") perm = await Notification.requestPermission();
    if (perm === "granted") { setNotifEnabled(true); localStorage.setItem("am_notif", "1"); toast.success("Alertes activées : nouveaux blocages dans votre rayon"); }
    else toast.error("Autorisation de notification refusée");
  };

  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    localStorage.setItem("am_theme", next);
    const root = document.documentElement;
    if (next === "light") { root.classList.add("theme-light"); root.classList.remove("dark"); }
    else { root.classList.add("dark"); root.classList.remove("theme-light"); }
  };

  const openFromList = (ev) => { setSelected(ev); setFocusId(ev.id); };

  const dateStr = now.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "Europe/Paris" });
  const timeStr = now.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: "Europe/Paris" });

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100 flex flex-col">
      {/* Header */}
      <header data-testid="app-header" className="bg-[#0B0F17]/95 border-b border-slate-800 backdrop-blur-lg sticky top-0 z-[500]">
        <div className="px-2 pt-2 pb-1 flex items-center justify-between gap-2">
          <h1 className="font-black tracking-tight text-[18px] sm:text-[22px] leading-none text-slate-50 flex items-center gap-1.5 am-title truncate">
            <img src="/am-icon.png" alt="" className="h-6 w-6 rounded-md shrink-0" /> Alliance Manif
          </h1>
          <div className="flex items-center gap-1 shrink-0">
            <button data-testid="notif-toggle" onClick={toggleNotif} aria-label="Alertes blocages"
              className={`h-9 w-9 flex items-center justify-center rounded-lg ${notifEnabled ? "bg-sky-600 text-white" : "bg-slate-800 hover:bg-slate-700 text-slate-200"}`}>
              {notifEnabled ? <Bell size={16} /> : <BellOff size={16} />}
            </button>
            <button data-testid="theme-toggle" onClick={toggleTheme} aria-label="Basculer le thème clair ou sombre"
              className="h-9 w-9 flex items-center justify-center rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200">
              {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <Link to="/a-propos" data-testid="about-link" className="h-9 w-9 flex items-center justify-center rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200">
              <Info size={16} />
            </Link>
            <Link to="/admin" data-testid="admin-login-btn" className="h-9 px-2.5 flex items-center justify-center rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 gap-1 text-[13px] font-semibold">
              <Shield size={15} /> <span className="hidden sm:inline">Admin</span>
            </Link>
          </div>
        </div>

        {/* Date et heure en direct (Paris) — toujours visible, centrée */}
        <div data-testid="live-clock" className="px-2 pb-1 flex items-center justify-center gap-2 text-center">
          <span className="text-[11px] sm:text-xs font-semibold text-slate-300 capitalize">{dateStr}</span>
          <span className="text-[12px] sm:text-sm font-black tabular-nums text-[#d4af37] tracking-wider">{timeStr}</span>
        </div>

        {/* Onglets de type (ligne fine défilante, soulignement or sur l'actif) + bouton Proposer */}
        <div className="px-2 flex items-center gap-1 border-b border-slate-800" style={{ height: 40 }}>
          <div className="flex items-center gap-1 overflow-x-auto no-scrollbar flex-1 min-w-0">
          {[["", "Tout", "#d4af37"], ["manifestation", "Manifestations", "#2563EB"], ["blocage_routier", "Blocages", "#DC2626"], ["perturbation_transport", "Transports", "#EA580C"]].map(([val, label, color]) => (
            <button key={val || "all"} data-testid={`type-tab-${val || "all"}`} onClick={() => setFilters((f) => ({ ...f, type: val }))}
              className={`shrink-0 h-[38px] px-2.5 inline-flex items-center gap-1.5 text-[13px] font-semibold whitespace-nowrap border-b-2 ${filters.type === val ? "border-[#d4af37] text-slate-50" : "border-transparent text-slate-400"}`}>
              <span className="h-2 w-2 rounded-full" style={{ background: color }} /> {label}
            </button>
          ))}
          </div>
          <button data-testid="propose-btn" onClick={() => setShowPropose(true)}
            className="shrink-0 h-8 px-3 flex items-center justify-center rounded-full bg-sky-600 hover:bg-sky-500 active:scale-95 transition text-white gap-1.5 text-[13px] font-bold shadow-lg shadow-sky-900/40">
            <Megaphone size={15} /> Proposer
          </button>
        </div>

        {/* Puces de journée défilantes */}
        <div data-testid="mobilization-day-select" className="px-2 py-1.5 flex items-center gap-1.5 overflow-x-auto no-scrollbar">
          {days.map((d) => (
            <button key={d.id} data-testid={`day-chip-${d.id}`} onClick={() => setDayId(d.id)}
              className={`shrink-0 h-8 px-3 rounded-full text-[12px] font-semibold whitespace-nowrap border ${dayId === d.id ? "bg-sky-600 text-white border-sky-500" : "bg-[#151C28] text-slate-300 border-slate-700"}`}>
              {d.name}{d.status === "en_cours" ? " • en cours" : d.status === "archivee" ? " • archivée" : d.status === "terminee" ? " • terminée" : ""}
            </button>
          ))}
        </div>

        {/* Recherche fine + bascule carte/liste */}
        <div className="px-2 pb-2 flex items-center gap-1.5">
          <div className="flex-1 min-w-0"><AroundMeBar setUserLoc={setUserLoc} radius={radius} setRadius={setRadius} /></div>
          <div className="flex rounded-lg overflow-hidden border border-slate-700 shrink-0">
            <button data-testid="map-view-toggle" onClick={() => setView("map")}
              className={`h-9 px-2.5 flex items-center gap-1 text-[13px] font-semibold ${view === "map" ? "bg-sky-600 text-white" : "bg-[#151C28] text-slate-300"}`}>
              <MapIcon size={15} /> <span className="hidden sm:inline">Carte</span>
            </button>
            <button data-testid="list-view-toggle" onClick={() => setView("list")}
              className={`h-9 px-2.5 flex items-center gap-1 text-[13px] font-semibold ${view === "list" ? "bg-sky-600 text-white" : "bg-[#151C28] text-slate-300"}`}>
              <List size={15} /> <span className="hidden sm:inline">Liste</span>
            </button>
          </div>
        </div>
        {installPrompt && (
          <div className="px-2 pb-2">
            <button data-testid="pwa-install-btn" onClick={doInstall} className="text-xs text-sky-400 hover:underline font-semibold inline-flex items-center gap-1">
              <Download size={13} /> Installer l'application
            </button>
          </div>
        )}
      </header>

      {isArchive && (
        <div data-testid="archive-banner" className="px-4 py-2 bg-slate-800/70 border-b border-slate-700 text-xs text-slate-300 flex items-center gap-2">
          <span className="text-[10px] uppercase font-extrabold tracking-widest px-2 py-0.5 rounded-full bg-slate-700 text-slate-200">Archive</span>
          Journée passée — carte figée et liste conservées. Détails purgés après 30 jours.
        </div>
      )}

      {activeDay?.description && (
        <div className="px-4 py-2 bg-[#151C28] border-b border-slate-800 text-xs text-slate-400">{activeDay.description}</div>
      )}

      {/* Content */}
      <main className="flex-1 relative">
        <div className={view === "map" ? "absolute inset-0" : "hidden"}>
          <MapView events={mapEvents} userLoc={userLoc} radius={radius} onSelect={setSelected} focusId={focusId} active={view === "map"} />
          <div data-testid="map-legend" className="absolute top-3 right-3 z-[600] bg-[#0B0F17]/88 border border-slate-700 rounded-xl px-3 py-2 backdrop-blur space-y-1">
            {Object.entries(RELIABILITY).map(([k, r]) => (
              <div key={k} className="flex items-center gap-2 text-[10px] text-slate-300">
                <span className="h-3 w-3 rounded-full inline-flex items-center justify-center text-[8px] text-white" style={{ background: r.color, border: k === "confirme" ? "2px solid #F59E0B" : k === "annonce" ? "1.5px dashed #7C2D12" : "1.5px solid #6D5B8E", opacity: k === "reseaux_sociaux" ? 0.75 : 1 }} />
                <span className="font-semibold">{r.label}</span>
              </div>
            ))}
          </div>
          <div data-testid="legal-disclaimer" className="absolute bottom-3 left-3 right-3 sm:right-auto sm:max-w-sm z-[600] bg-[#0B0F17]/88 border border-slate-700 rounded-xl px-3 py-2 backdrop-blur">
            <p className="text-[11px] text-slate-200 font-semibold">{mapEvents.length} événement(s){userLoc ? ` · ${radius} km` : ""}</p>
            <p className="text-[10px] text-slate-400 leading-snug">Informations indicatives issues de sources publiques. Votre position GPS n'est jamais stockée. Vérifiez l'info trafic officielle avant de partir.</p>
          </div>
        </div>
        {view === "list" && (
          <div className="px-4 py-4 space-y-4 max-w-3xl mx-auto">
            <Filters filters={filters} setFilters={setFilters} departments={departments} />
            <p className="text-xs text-slate-500">{listEvents.length} résultat(s){userLoc ? `, triés par distance` : ""}</p>
            {listEvents.length === 0 && <p className="text-sm text-slate-400 py-8 text-center">Aucun événement pour ces critères.</p>}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {listEvents.map((e) => <EventCard key={e.id} event={e} onOpen={openFromList} />)}
            </div>
            <LegalDisclaimer />
          </div>
        )}
      </main>

      <EventSheet event={selected} onClose={() => setSelected(null)} />
      <ProposeDialog open={showPropose} onClose={() => setShowPropose(false)} defaultType={filters.type} />
      <Footer />
    </div>
  );
}

function LegalDisclaimer() {
  return (
    <div data-testid="legal-disclaimer-list" className="bg-slate-900 border border-slate-800 p-4 rounded-xl text-xs text-slate-400 space-y-1 mt-6">
      <p className="font-semibold text-slate-300">Avertissement</p>
      <p>Alliance Manif diffuse des informations indicatives issues de sources publiques. Respectez la loi et les consignes des autorités. Chaque information affiche sa source et son heure de mise à jour.</p>
      <p>Aucune donnée personnelle n'est collectée : votre position GPS reste sur votre téléphone, elle n'est jamais stockée ni transmise.</p>
    </div>
  );
}
